import { Dialog } from '@base-ui-components/react/dialog';
import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { createClassNames } from '@hrworks/design-system/functions';
import { mq, shouldForwardProp } from '@hrworks/design-system/utilities';
import type { Size } from '@hrworks/sui-shared/types';

import { Scroller } from '../Scroller';
import { Title as _Title } from '../Title';

const componentConfig = (theme: Theme) => ({
  sizes: {
    extraSmall: 300,
    small: 650,
    medium: 1000,
    large: 1350,
    extraLarge: 1700,
    auto: 'max-content',
  },
  contentPadding: theme.eclipseTier1.spacing['24px'],
  get viewportPadding() {
    return `calc(${this.contentPadding} + 1px)`;
  },
});

type ModalType = {
  $size: Size | 'auto';
  $fullHeight?: boolean;
  $fullWidth?: boolean;
  $fullScreen?: boolean;
};

const Modal = styled(Dialog.Popup, { shouldForwardProp })<ModalType>(
  ({ theme, $size, $fullHeight, $fullWidth, $fullScreen }) => {
    const { sizes, viewportPadding } = componentConfig(theme);

    return [
      !$fullScreen && { boxShadow: theme.eclipseTier2Color.elevation.shadow.level1.boxShadow },
      {
        position: 'fixed',
        top: '50%',
        left: '50%',
        translate: '-50% -50%',
        scale: '0.95',
        zIndex: theme.marko.variables.zIndex.modal,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.eclipseTier2Color.surface.raised,
        backgroundClip: 'padding-box',
        pointerEvents: 'auto',
        outline: 'none',
        opacity: 0,
        [mq.conditionalTransition]: {
          // TODO: Migrate animationDuration to eclipse tokens (no eclipse motion tokens yet)
          transition: `opacity ${theme.marko.variables.animationDuration.long}, scale ${theme.marko.variables.animationDuration.long}`,
        },
        '&[data-open]': {
          scale: '1',
          opacity: 1,
        },
        '&[data-starting-style]': {
          scale: '0.95',
          opacity: 0,
        },
        '&[data-ending-style]': {
          scale: '0.95',
          opacity: 0,
        },
        ...($fullScreen
          ? {
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              translate: 'none',
              scale: '1',
              opacity: 1,
              '&[data-open]': { scale: '1', opacity: 1 },
              '&[data-starting-style]': { opacity: 0 },
              '&[data-ending-style]': { opacity: 0 },
            }
          : {
              borderRadius: theme.eclipseTier1.radius['8px'],
              border: `1px solid ${theme.eclipseTier2Color.border.bold}`,
              maxWidth: `calc(100svw - ${viewportPadding})`,
              maxHeight: `calc(100svh - ${viewportPadding})`,
              marginBottom: 'env(safe-area-inset-bottom)',
              width: $fullWidth ? '100%' : sizes[$size],
              ...($fullHeight && {
                height: '100%',
              }),
            }),
      },
    ];
  },
);

const Backdrop = styled.div(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  backgroundColor: theme.eclipseTier2Color.elevation.blanket,
  zIndex: theme.marko.variables.zIndex.modal,
  opacity: 0,
  [mq.conditionalTransition]: {
    // TODO: Migrate animationDuration to eclipse tokens (no eclipse motion tokens yet)
    transition: `opacity ${theme.marko.variables.animationDuration.normal}`,
  },
  '&[data-open]': { opacity: 1 },
  '&[data-starting-style]': { opacity: 0 },
  '&[data-ending-style]': { opacity: 0 },
}));

const ScrollerContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflowY: 'hidden',
});

const CloseButton = styled(Dialog.Close)(({ theme }) => ({
  position: 'absolute',
  top: theme.eclipseTier1.spacing['20px'],
  right: theme.eclipseTier1.spacing['20px'],
  ':focus-visible': {
    position: 'absolute',
    zIndex: 1,
  },
}));

const TitleScroller = styled(Scroller)({
  height: 'auto',
});

const Title = styled(_Title)({
  lineHeight: 1.5,
});

const components = { Modal, Backdrop, ScrollerContainer, CloseButton, TitleScroller, Title };

export const S = {
  componentConfig,
  ...components,
  ...createClassNames('Modal', components),
} as const;
